<script>
	// [대화 설정] 탭 — 챗 전체에 걸리는, 한 번 정하면 잘 안 바꾸는 값들. (V3 전용 — BuilderSidebar 필터)
	//   ① 이 채팅의 용도   ② 누구와 말하나(학습자 정보·수집 항목)   ③ 어떻게 말하나(유도 방식·쓰는 말·받아 주기)
	// 예전엔 전부 [블록] 탭 위쪽에 쌓여 있어서, 레슨을 고치러 갈 때마다 설정을 지나 스크롤해야 했다.
	// [블록] 탭은 이제 콘텐츠(자유 블록·플로우·코스·스텝)만 갖고, 스텝별 덮어쓰기는 스텝 카드에 남는다.
	// ⚠️ 상태는 전부 공유 chat 객체(chat.traits·collectKeys·guideStyle·replyStyle·purpose)에 산다 —
	//    [블록] 탭의 {{변수}} 칩·학습자별 내용 칸도 같은 chat.traits 에서 계산하므로 탭이 갈려도 그대로 맞는다.
	// ⚠️ 바꾸면 바로 저장한다(chat.save = 챗 전체 전송, learnerDecls.js 가 보낼 때 정리).
	import { PRConnect } from "@projectroom/ui-lib";
	import { Section, Select, Switch } from "../../lib/ui";
	import { autoGrow } from "./autoGrow";
	import { NICKNAME_KEY, knownValues } from "./learnerDecls";
	import ReplyStylePicker from "./ReplyStylePicker.svelte";
	import { ANSWER_MODELS } from "./aiModels";
	import { levelGuideRuleHit } from "./stepLint";

	export let chat;
	const tab = "conversation";

	// ── ① 용도 — 이 값 하나가 [블록] 탭 화면을 통째로 정한다(테스트 챗이면 판정 기준·완료 화면만).
	$: isTestChat = chat.purpose === "intake" || chat.purpose === "exit";
	const savePurpose = () => {
		chat = chat;
		chat.save();   // save 가 chat.assign() 으로 부모를 다시 그려 [블록] 탭도 바뀐 용도를 본다
	};

	// ── 저장 공통 — 학습자 정보·수집 항목은 바꾸는 즉시 저장(글 칸은 칸을 벗어날 때).
	//    빈 행·빈 지시문은 화면에만 남고 서버로는 정리돼 나간다(learnerDecls.js ← Chat.save).
	const persistLearner = () => {
		chat = chat;
		chat.save();
	};

	// ── ② 수집 항목(챗 단위): 대화 어디서든 이 정보가 나오면 학습자별로 캐치해 저장한다.
	//    블록별로 흩어 두지 않는 이유 — 대화가 어디로 튈지 모르므로 "나오면 잡는다"가 맞다.
	//    서버는 이 키 목록을 셀렉터·이해콜·가이드 콜 프롬프트에 실어 보낸다(추가 LLM 콜 0).
	// 빈 행은 저장할 게 없다 — 키를 적고 칸을 벗어날 때 저장된다
	const addCollectKey = () => {
		chat.collectKeys = [...(chat.collectKeys || []), { key: "", desc: "" }];
		chat = chat;
	};
	const removeCollectKey = (i) => {
		chat.collectKeys = (chat.collectKeys || []).filter((_, k) => k !== i);
		persistLearner();
	};

	// ── ② 학습자 정보: 이 챗이 학습자 프로필과 맺는 관계. 한 항목이 세 가지를 담는다.
	//    write  이 챗이 대화에서 잡아 남긴다(사전 설정 챗)
	//    guide  이 값에 따라 말하는 방식을 바꾼다(레슨 챗) — 값별(map) 또는 템플릿
	//    둘 다 없으면 값만 프롬프트에 실린다("무엇을 아는가"와 "어떻게 말할까"는 다른 일)
	//    ⚠️ write 를 켠 챗만 그 키를 저장한다. 허브 게이트가 '값이 있나'로 판정하므로,
	//       레슨 챗에서 우연히 키가 잡혀도 프로필을 덮지 못하게 하는 오염 방어다.
	// 지시문 편집 모드: "" 없음 / "map" 값별 / "template" 템플릿
	// 사전 테스트에 연결된 항목은 늘 '값별로' — 레벨이 정해져 있으니 레벨마다 적는다.
	const guideMode = (t) => (t?.from?.chatId || t?.guide?.map ? "map" : t?.guide?.template != null ? "template" : "");
	const setGuideMode = (i, mode) => {
		const t = chat.traits[i];
		t.guide = mode === "map" ? { map: t.guide?.map || {} } : mode === "template" ? { template: t.guide?.template || "" } : null;
		persistLearner();
	};
	// map 은 {값: 지시문} 객체지만 편집 중에는 순서·빈 키가 필요해 배열로 다룬다.
	// ⚠️ 연결된 항목은 레벨 목록(values)으로 행을 만든다. 저장은 빈 지시문을 버리므로 map 으로 그리면
	//    지시문을 안 적은 레벨의 행이 저장 뒤 사라져 다시 적을 수 없게 된다.
	const mapRows = (t) =>
		t?.from?.chatId && Array.isArray(t.values) && t.values.length
			? t.values.map((v) => [v, t.guide?.map?.[v] ?? ""])
			: Object.entries(t?.guide?.map || {});
	const setMapRow = (i, oldVal, newVal, line) => {
		const m = { ...(chat.traits[i].guide?.map || {}) };
		if (oldVal !== newVal) delete m[oldVal];
		m[newVal] = line;
		chat.traits[i].guide = { map: m };
		persistLearner();
	};
	const addMapRow = (i) => {
		chat.traits[i].guide = { map: { ...(chat.traits[i].guide?.map || {}), "": "" } };
		chat = chat;
	};
	const removeMapRow = (i, val) => {
		const m = { ...(chat.traits[i].guide?.map || {}) };
		delete m[val];
		chat.traits[i].guide = { map: m };
		persistLearner();
	};
	// ⭐ 닉네임은 '기본 제공 입력'이라 따로 한 줄로 보여 준다(키를 바꾸거나 '받기'를 켤 수 없다).
	//    값은 허브 로그인 신원에서 오고, 여기서는 로그인 안 한 학습자·프리뷰에서 쓸 기본값만 정한다.
	$: nickDecl = (chat.traits || []).find((t) => t.key === NICKNAME_KEY) || null;
	const setNicknameDefault = (v) => {
		const list = chat.traits || [];
		const i = list.findIndex((t) => t.key === NICKNAME_KEY);
		if (i >= 0) list[i] = { ...list[i], default: v };
		else list.push({ key: NICKNAME_KEY, desc: "계정 닉네임(허브 로그인)", default: v });
		chat.traits = list;
		persistLearner();
	};

	// ── 사전 테스트 연결 ─────────────────────────────────────────────────────────
	// 레슨 챗이 레벨 값을 "어느 테스트에서 받는지"를 저작 화면에 박아 둔다.
	// ⚠️ 런타임 연결은 지금도 **키 이름 + 구간 라벨 글자 그대로**뿐이다(서버 TraitStore). 손으로 적으면
	//    '🍃' 와 '나뭇잎' 같은 차이로 아무 오류 없이 레벨 지시·레벨별 낱말이 통째로 빠진다.
	//    → 테스트에서 키·구간을 가져와 그 틀릴 자리를 없앤다. 가져온 키·구간은 여기서 고치지 못한다.
	let levelTests = [];
	let levelTestsLoaded = false;
	// 응답이 온 뒤에만 셀렉트를 그린다 — 옵션이 늦게 바뀌면 선택된 <option> 이 갈아끼워지면서
	// 브라우저가 첫 옵션('연결 안 함')을 고른 채로 남는다(Svelte 는 value 가 안 바뀌면 다시 맞추지 않는다).
	let levelTestsReady = false;
	const loadLevelTests = () => {
		levelTestsLoaded = true;
		PRConnect.send(
			"/api/Chat/getLevelTests",
			{},
			(data) => {
				if (data.header.status === "success") levelTests = (data.result || []).filter((t) => t.chatId !== chat.chatId);
				levelTestsReady = true;
			},
			() => (levelTestsReady = true),
		);
	};
	// 탭을 처음 열 때 한 번만 부른다(예전엔 접힌 카드를 펼칠 때였다)
	$: if (chat.isCurrentTab(tab) && !levelTestsLoaded && !isTestChat) loadLevelTests();
	const testOf = (t) => levelTests.find((x) => x.chatId === t?.from?.chatId) || null;
	const labelsOf = (test) => (test?.tiers || []).map((x) => x.label);
	// 연결 뒤에 테스트를 고쳤나(구간·키가 달라졌나) — 다르면 테스트 쪽 구간을 돌려준다
	const linkDrift = (t) => {
		const test = testOf(t);
		if (!test) return null;
		return labelsOf(test).join(" ") === knownValues(t).join(" ") && test.resultKey === t.key ? null : labelsOf(test).join(" ");
	};
	// 셀렉트 옵션 글자 — 키 이름(englishTier)은 저작자가 고를 때 몰라도 되는 값이라 빼고, 레벨만 보인다
	const testLabel = (test) =>
		`${test.title || "제목 없는 테스트"}${test.purpose === "exit" ? " (사후 테스트)" : ""}` +
		(labelsOf(test).length ? ` — ${labelsOf(test).join(" · ")}` : " — 구간 없음");
	// ⭐ 이 채팅의 '레벨 출처' = 테스트에 연결된 첫 항목. [레벨 받아오기] 셀렉트는 이 값을 그대로 보여 준다.
	$: levelLink = (chat.traits || []).find((t) => t.from?.chatId) || null;
	const chooseLevelTest = (chatId) => {
		const cur = levelLink?.from?.chatId || "";
		if (chatId === cur) return;
		// 바꾸거나 끊을 때 기존 항목은 지우지 않고 연결만 푼다 — 적어 둔 레벨별 지시문이 남는다.
		// 같은 키를 쓰는 테스트로 바꾸면 linkTest 가 그 항목을 그대로 이어 받아 같은 레벨의 지시문도 살아남는다.
		if (levelLink) unlinkTest(chat.traits.indexOf(levelLink));
		if (chatId) linkTest(chatId);
		persistLearner();
	};
	const linkTest = (chatId) => {
		const test = levelTests.find((x) => x.chatId === chatId);
		if (!test) return;
		const list = [...(chat.traits || [])];
		let i = list.findIndex((t) => t.from?.chatId === test.chatId);
		if (i < 0) i = list.findIndex((t) => t.key === test.resultKey);
		const cur = i >= 0 ? list[i] : { key: test.resultKey, desc: "" };
		const labels = labelsOf(test);
		const oldMap = cur.guide?.map || {};
		const next = {
			...cur,
			key: test.resultKey,
			desc: cur.desc || test.resultDesc || `${test.title} 결과`,
			write: false, // 레슨 챗은 읽기만 — 값은 테스트가 남긴다
			values: labels,
			guide: { map: Object.fromEntries(labels.map((v) => [v, oldMap[v] ?? ""])) },
			// 값이 없는 학습자(테스트 전·프리뷰)는 가운데 레벨로 — 저작자가 바꿀 수 있다
			default: labels.includes(cur.default) ? cur.default : labels[Math.floor((labels.length - 1) / 2)] || "",
			from: { chatId: test.chatId, title: test.title },
		};
		if (i >= 0) list[i] = next;
		else list.push(next);
		chat.traits = list;
		chat = chat;
	};
	const unlinkTest = (ti) => {
		if (ti < 0) return;
		const { from, values, ...rest } = chat.traits[ti];
		chat.traits[ti] = rest;
		chat = chat;
	};
	const setLevelDefault = (v) => {
		const i = chat.traits.indexOf(levelLink);
		if (i < 0) return;
		chat.traits[i] = { ...chat.traits[i], default: v };
		persistLearner();
	};
	// 레벨마다 말하는 방식 — 레벨 라벨은 테스트에서 오므로 지시문만 고친다
	const setLevelLine = (level, line) => {
		const i = chat.traits.indexOf(levelLink);
		if (i < 0) return;
		chat.traits[i].guide = { map: { ...(chat.traits[i].guide?.map || {}), [level]: line } };
		persistLearner();
	};

	const addTrait = () => {
		chat.traits = [...(chat.traits || []), { key: "", desc: "", write: false, guide: null }];
		chat = chat;
	};
	const removeTrait = (i) => {
		chat.traits = (chat.traits || []).filter((_, k) => k !== i);
		persistLearner();
	};
	const toggleWrite = (i) => {
		chat.traits[i].write = !chat.traits[i].write;
		persistLearner();
	};
	// 글 칸은 bind 대신 값+on:change — 목록을 {t, i} 로 감싸 그리므로 원본(chat.traits[i])을 직접 고친다
	const setTraitField = (i, field, v) => {
		chat.traits[i] = { ...chat.traits[i], [field]: v };
		persistLearner();
	};
	const setTemplate = (i, v) => {
		chat.traits[i].guide = { template: v };
		persistLearner();
	};
	// [그 밖의 학습자 정보] 목록 = 닉네임(따로 한 줄)과 레벨 출처(위 레벨 칸)를 뺀 나머지.
	// ⚠️ 인덱스는 chat.traits 기준으로 들고 다닌다 — 핸들러들이 chat.traits[i] 를 고친다.
	$: otherTraits = (chat.traits || [])
		.map((t, i) => ({ t, i }))
		.filter(({ t }) => t.key !== NICKNAME_KEY && t !== levelLink);

	// ── ③ 어떻게 말하나 — 챗 기본값. 스텝 카드에서 축 단위로 덮어쓸 수 있다([블록] 탭).
	// 유도 방식(CHATS.GUIDE_STYLE). ''(=null)이면 서버 내장 minimal.
	const setChatGuideStyle = (v) => {
		chat.guideStyle = v || null;
		chat = chat;
		chat.save();
	};
	// 쓰는 말·학습자 말 받아 주기(CHATS.REPLY_STYLE, replyStyles.js ↔ 서버 chat-core/block/replyStyles.js).
	//   예전엔 콘텐츠가 이 규칙을 [이 스텝의 상황] 글·페르소나에 적어 넣었다 — 이제 고르면 엔진이 싣는다.
	const setChatReplyStyle = (v) => {
		chat.replyStyle = v;
		chat = chat;
		chat.save();
	};
	// 답변 모델(CHATS.AI_MODEL) — 위 칸들이 정한 지시를 '얼마나 지키나'는 이 모델이 정한다. "" = 서버 전역 설정.
	const setChatAiModel = (v) => {
		chat.aiModel = v || "";
		chat = chat;
		chat.save();
	};
</script>

{#if chat.isCurrentTab(tab)}
	<div class="conv-tab tw-space-y-4 tw-font-sans tw-text-foreground">
		<!-- ① 이 채팅이 무엇을 하는 채팅인가 — [블록] 탭 화면을 통째로 정한다 -->
		<Section title="이 채팅의 용도" description="고르는 용도에 따라 [블록] 탭에서 만들 것이 달라져요.">
			<div class="tw-max-w-md">
				<Select class="tw-w-full" bind:value={chat.purpose} on:change={savePurpose}>
					<option value="normal">일반 대화</option>
					<option value="intake">사전 테스트 — 메인 전에 수준을 확인</option>
					<option value="exit">사후 테스트 — 마친 뒤 다시 확인</option>
				</Select>
			</div>
			{#if isTestChat}
				<p class="sec-note">
					테스트 채팅은 [블록] 탭에서 <b>판정 기준</b>과 <b>완료 화면</b>만 정하면 됩니다.
					결과는 프로필에 남아 다른 채팅들의 대화를 바꾸고, 사전 테스트는 허브 격자를 여는 열쇠가 돼요.
				</p>
			{/if}
		</Section>

		{#if !isTestChat}
			<!-- ② 누구와 말하나 — 학습자 정보 = 채팅을 넘어 이어지는 값(LEARNER_TRAITS) / 수집 항목 = 이 채팅 안에서만(CHAT_LEARNER_FACTS) -->
			<Section title="학습자 정보" description="레벨·닉네임처럼 채팅을 넘어 이어지는 값으로 학습자마다 대화를 맞춰요. 바꾸면 바로 저장돼요.">
				<!-- 레벨 받아오기(사전 테스트 연결) — 테스트가 남기는 키·구간을 그대로 가져온다(손으로 적다 어긋나는 일 방지).
				     셀렉트 값 = 지금 연결된 테스트. 바꾸면 갈아타고, '연결 안 함'이면 연결만 푼다(지시문은 남는다). -->
				<section class="lp-sec">
					<div class="lp-sec-title">레벨 받아오기</div>
					<div class="lp-sec-desc">사전 테스트 결과(레벨)를 이어 받아 레벨에 맞는 말투로 대화해요.</div>
					{#if levelTestsReady}
						<div class="lp-select">
							<Select
								class="tw-w-full"
								value={levelLink?.from?.chatId || ""}
								disabled={!levelTests.length && !levelLink}
								on:change={(e) => chooseLevelTest(e.target.value)}
							>
								<option value="">{levelTests.length || levelLink ? "연결 안 함" : "연결할 사전 테스트가 없어요"}</option>
								{#each levelTests as t (t.chatId)}
									<option value={t.chatId}>{testLabel(t)}</option>
								{/each}
								{#if levelLink && !testOf(levelLink)}
									<option value={levelLink.from.chatId}>{levelLink.from.title || levelLink.from.chatId} (찾을 수 없음)</option>
								{/if}
							</Select>
						</div>
					{:else}
						<div class="lp-hint">테스트 목록을 불러오는 중…</div>
					{/if}

					{#if levelLink}
						<div class="lp-meta">
							지침에서 <code>{`{{${levelLink.key}}}`}</code> 로 이 학습자의 레벨을 쓸 수 있어요.
						</div>
						{#if levelTestsReady && !testOf(levelLink)}
							<div class="lp-warn">연결된 테스트를 찾을 수 없어요(지워졌거나 다른 계정의 채팅). 레벨은 마지막으로 가져온 그대로예요.</div>
						{:else if linkDrift(levelLink)}
							<div class="lp-warn">
								<span>테스트의 레벨 구간이 바뀌었어요 → {linkDrift(levelLink)}</span>
								<button
									type="button"
									class="mini"
									on:click={() => {
										linkTest(levelLink.from.chatId);
										persistLearner();
									}}>다시 맞추기</button
								>
							</div>
						{/if}
						{#if knownValues(levelLink).length}
							<div class="lp-field-label">레벨마다 말하는 방식</div>
							{#each knownValues(levelLink) as lv (lv)}
								{@const hit = levelGuideRuleHit(levelLink.guide?.map?.[lv])}
								<div class="lp-level-row">
									<span class="lp-level">{lv}</span>
									<textarea
										class="text-field"
										rows="1"
										placeholder="이 레벨에서 말하는 방식 — 문장 길이·어휘 수준·질문 깊이"
										value={levelLink.guide?.map?.[lv] ?? ""}
										use:autoGrow={levelLink.guide?.map?.[lv]}
										on:change={(e) => setLevelLine(lv, e.currentTarget.value)}
									/>
								</div>
								{#if hit}
									<div class="lp-rule-warn">
										‘{hit}’ — 레벨 지시문에는 봇 말의 난이도(문장 길이·어휘·문법)만 적어 주세요. 학습자 답을 받는 방식이나
										언어는 아래 [쓰는 말 · 학습자 말 받아 주기]가 맡아요. 둘 다 적으면 같은 대화에서 서로 부딪혀요.
									</div>
								{/if}
							{/each}
							<div class="lp-inline">
								<span>테스트를 아직 안 본 학습자·프리뷰는</span>
								<div class="lp-select-sm">
									<Select class="tw-w-full" value={levelLink.default || ""} on:change={(e) => setLevelDefault(e.target.value)}>
										{#if !knownValues(levelLink).includes(levelLink.default)}<option value="">고르기</option>{/if}
										{#each knownValues(levelLink) as lv (lv)}<option value={lv}>{lv}</option>{/each}
									</Select>
								</div>
								<span>레벨로 대화해요</span>
							</div>
						{:else}
							<div class="lp-warn">이 테스트에는 레벨 구간이 아직 없어요 — 테스트 채팅의 판정 스텝에서 구간을 먼저 정해 주세요.</div>
						{/if}
					{:else if levelTestsReady && !levelTests.length}
						<div class="lp-hint">
							<b>이 채팅의 용도</b>를 ‘사전 테스트’로 둔 채팅에 판정 스텝을 만들면 여기서 고를 수 있어요.
						</div>
					{:else if levelTestsReady}
						<div class="lp-hint">
							테스트를 고르면 레벨이 그대로 들어오고, 레벨마다 <b>말하는 방식</b>과 스텝의 <b>레벨별 낱말</b>을 적을 수 있어요.
						</div>
					{/if}
				</section>

				<!-- 기본 제공: 닉네임 — 값은 허브 로그인 신원에서 온다. 여기서는 로그인 전·프리뷰 기본값만 -->
				<section class="lp-sec">
					<div class="lp-sec-title">닉네임 <code>{"{{nickname}}"}</code></div>
					<div class="lp-sec-desc">허브에 로그인한 학습자의 닉네임이 자동으로 들어가요.</div>
					<div class="lp-inline">
						<span>로그인 전·프리뷰에서는</span>
						<input
							class="text-field lp-short"
							type="text"
							placeholder="예: 친구"
							value={nickDecl?.default ?? ""}
							on:change={(e) => setNicknameDefault(e.currentTarget.value.trim())}
						/>
						<span>(이)라고 불러요</span>
					</div>
				</section>

				<section class="lp-sec">
					<div class="lp-sec-title">그 밖의 학습자 정보</div>
					<div class="lp-sec-desc">
						이름·관계·관심사처럼 채팅을 넘어 이어 쓰는 값이에요. 지침·고정 문구에 <code>{"{{키}}"}</code> 로
						넣으면 이 학습자의 값으로 바뀌고, 값이 없으면 기본값이 들어가요.
					</div>
					{#each otherTraits as { t: tk, i: ti }}
						<div class="lp-item" class:linked={tk.from?.chatId}>
							<div class="lp-item-row">
								<input
									class="text-field lp-key"
									type="text"
									placeholder="키 — 예: favoriteFood"
									value={tk.key}
									disabled={!!tk.from?.chatId}
									on:change={(e) => setTraitField(ti, "key", e.currentTarget.value.trim())}
								/>
								<input
									class="text-field"
									type="text"
									placeholder="설명 — 무엇을 담는 값인지"
									value={tk.desc || ""}
									on:change={(e) => setTraitField(ti, "desc", e.currentTarget.value)}
								/>
								<button type="button" class="mini del" on:click={() => removeTrait(ti)}>삭제</button>
							</div>
							{#if tk.from?.chatId}
								<div class="lp-item-note">
									<span>🔗 {tk.from.title || tk.from.chatId} 에서 이어 받는 값</span>
									<button
										type="button"
										class="mini"
										title="키·값을 직접 고칠 수 있게 풀어요(지시문은 그대로)"
										on:click={() => {
											unlinkTest(ti);
											persistLearner();
										}}>연결 풀기</button
									>
								</div>
							{:else}
								<div class="lp-item-opts">
									<label class="lp-switch">
										<Switch checked={!!tk.write} on:click={() => toggleWrite(ti)} />
										<span>이 채팅 대화에서 값 받기</span>
									</label>
									<div class="lp-opt">
										<span>대화 바꾸기</span>
										<div class="lp-select-md">
											<Select class="tw-w-full" value={guideMode(tk)} on:change={(e) => setGuideMode(ti, e.target.value)}>
												<option value="">안 바꿈 — 값만 참고</option>
												<option value="map">값마다 지시문 따로</option>
												<option value="template">한 문장에 값 넣기</option>
											</Select>
										</div>
									</div>
									<div class="lp-opt">
										<span>값이 없을 때</span>
										<input
											class="text-field lp-short"
											type="text"
											list={`trait-values-${ti}`}
											placeholder="기본값"
											value={tk.default ?? ""}
											on:change={(e) => setTraitField(ti, "default", e.currentTarget.value.trim())}
										/>
										<datalist id={`trait-values-${ti}`}>
											{#each knownValues(tk) as v}<option value={v} />{/each}
										</datalist>
									</div>
								</div>
								{#if tk.write}
									<div class="lp-item-hint">대화 중 이 값이 나오면 학습자 프로필에 남겨요 — 다른 채팅이 이어 받아 써요.</div>
								{/if}
							{/if}
							{#if guideMode(tk) === "map"}
								<div class="lp-map">
									{#each mapRows(tk) as [val, line] (val)}
										{@const hit = levelGuideRuleHit(line)}
										<div class="lp-level-row">
											<input
												class="text-field lp-val"
												type="text"
												placeholder="값 — 예: 초급"
												value={val}
												disabled={!!tk.from?.chatId}
												on:change={(e) => setMapRow(ti, val, e.currentTarget.value.trim(), line)}
											/>
											<textarea
												class="text-field"
												rows="1"
												placeholder="이 값일 때 어떻게 말할지 — 예: 문장은 8단어 이내로, 어려운 단어는 피하기"
												value={line}
												use:autoGrow={line}
												on:change={(e) => setMapRow(ti, val, val, e.currentTarget.value)}
											/>
											{#if !tk.from?.chatId}
												<button type="button" class="mini del" on:click={() => removeMapRow(ti, val)}>삭제</button>
											{/if}
										</div>
										{#if hit}
											<div class="lp-rule-warn">‘{hit}’ — 받는 방식·언어는 [쓰는 말 · 학습자 말 받아 주기]에서 골라 주세요.</div>
										{/if}
									{/each}
									{#if !tk.from?.chatId}
										<button type="button" class="mini add" on:click={() => addMapRow(ti)}>+ 값 추가</button>
									{/if}
								</div>
							{:else if guideMode(tk) === "template"}
								<div class="lp-map">
									<input
										class="text-field"
										type="text"
										placeholder={"값이 들어갈 자리에 {v} — 예: 상대의 이름은 {v}. 이름으로 부를 것."}
										value={tk.guide?.template ?? ""}
										on:change={(e) => setTemplate(ti, e.currentTarget.value)}
									/>
								</div>
							{/if}
						</div>
					{/each}
					<button type="button" class="mini add" on:click={addTrait}>+ 학습자 정보 추가</button>
				</section>
			</Section>

			<!-- 수집 항목: 폼 없이 대화로 받는 정보. 자유 블록/플로우 어디서 나오든 잡힌다. -->
			<Section
				title="수집 항목"
				description="따로 묻지 않아도 대화 중 이 정보가 나오면 학습자별로 저장하고, 이후 대화에 다시 넣어 기억하게 해요. 이 채팅 안에서만 쓰고, 대화를 다시 시작해도 남아 있어요."
			>
				{#each chat.collectKeys || [] as ck, ci}
					<div class="lp-item-row">
						<input class="text-field lp-key" type="text" placeholder="키 — 예: 이름" bind:value={ck.key} on:change={persistLearner} />
						<input class="text-field" type="text" placeholder="설명 — 무엇을 받는 값인지" bind:value={ck.desc} on:change={persistLearner} />
						<button type="button" class="mini del" on:click={() => removeCollectKey(ci)}>삭제</button>
					</div>
				{/each}
				<button type="button" class="mini add" on:click={addCollectKey}>+ 항목 추가</button>
			</Section>

			<!-- ③ 어떻게 말하나 — 챗 기본값. 스텝 카드에서 축 단위로 덮어쓴다([블록] 탭). -->
			<Section title="유도 방식" description="레슨(플로우)의 유도 스텝에서 AI가 어떻게 이끌지의 기본값이에요. 스텝마다 다르게 정할 수 있어요.">
				<div class="tw-max-w-md">
					<Select class="tw-w-full" value={chat.guideStyle || ""} on:change={(e) => setChatGuideStyle(e.target.value)}>
						<option value="">최소 개입형 (기본값)</option>
						<option value="coach">코칭·유도형 — 정답 대신 스스로 하게, 부분 힌트</option>
						<option value="direct">직접 안내형 — 다음 할 일·예시를 직접 제시</option>
					</Select>
				</div>
			</Section>

			<!-- 쓰는 말은 레슨 밖 자유 대화에도 실리고(서버 BlockComposer), 받아 주기는 유도 스텝에만 실린다. -->
			<Section
				title="쓰는 말 · 학습자 말 받아 주기"
				description="봇이 어떤 말로 말할지(채팅 전체)와, 학습자가 이렇게 답했을 때 어떻게 받을지(유도 스텝)의 기본값이에요. 스텝마다 축 단위로 다르게 정할 수 있어요."
			>
				<ReplyStylePicker value={chat.replyStyle} showLanguage on:change={(e) => setChatReplyStyle(e.detail)} />
			</Section>
		{/if}

		<!-- ④ 누가 말하나 — 봇 대사를 쓰는 모델. 위 설정을 프롬프트로 조합하는 건 같고, 그 지시를 얼마나 지키는지가 모델마다 다르다.
		     테스트 챗도 대사를 쓰므로 용도와 상관없이 보인다. -->
		<Section
			title="답변 모델"
			description="봇 대사(첫 안내·유도·자유 답변)를 쓰는 AI 모델이에요. 비워 두면 서비스 기본 모델을 써요. 목표 판정·라우팅 모델은 바뀌지 않아요."
		>
			<div class="tw-max-w-md">
				<Select class="tw-w-full" value={chat.aiModel || ""} on:change={(e) => setChatAiModel(e.target.value)}>
					<option value="">서비스 기본 모델</option>
					{#each ANSWER_MODELS as m (m.name)}
						<option value={m.name}>{m.label}</option>
					{/each}
				</Select>
			</div>
		</Section>
	</div>
{/if}

<style>
	.conv-tab {
		padding: 10px;
	}
	.sec-note {
		font-size: 12px;
		line-height: 1.6;
		color: hsl(var(--sb-muted-foreground));
		margin: 10px 0 0;
	}
	.sec-note b {
		color: hsl(var(--sb-foreground));
	}
	/* ── 폼 컨트롤 — [블록] 탭(BlockContext)과 같은 규격(lib/ui shadcn Input/Select: h-10 · text-sm · ring focus) ── */
	.text-field {
		width: 100%;
		padding: 8px 12px;
		border: 1px solid hsl(var(--sb-input));
		border-radius: calc(var(--sb-radius) - 2px);
		font-size: 14px;
		line-height: 1.5;
		font-family: inherit;
		color: hsl(var(--sb-foreground));
		background: hsl(var(--sb-background));
		box-sizing: border-box;
		transition:
			border-color 0.15s ease,
			box-shadow 0.15s ease;
	}
	input.text-field {
		height: 40px;
	}
	.text-field::placeholder {
		color: hsl(var(--sb-muted-foreground));
	}
	.text-field:focus {
		outline: none;
		border-color: hsl(var(--sb-ring));
		box-shadow: 0 0 0 2px hsl(var(--sb-ring) / 0.35);
	}
	.text-field:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
	/* shadcn secondary 버튼 톤(작은 사이즈) — BlockContext 와 같은 모양 */
	button.mini {
		padding: 4px 10px;
		font-size: 12px;
		font-weight: 500;
		border: 1px solid hsl(var(--sb-border));
		border-radius: calc(var(--sb-radius) - 4px);
		background: hsl(var(--sb-secondary));
		color: hsl(var(--sb-secondary-foreground));
		transition: background-color 0.15s ease;
	}
	button.mini:hover:not(:disabled) {
		background: hsl(var(--sb-accent));
	}
	button.mini.add {
		background: transparent;
		border-style: dashed;
		color: hsl(var(--sb-primary));
	}
	button.mini.add:hover:not(:disabled) {
		background: hsl(var(--sb-primary) / 0.08);
	}
	button.mini.del {
		background: transparent;
		border-color: transparent;
		color: hsl(var(--sb-destructive));
	}
	button.mini.del:hover:not(:disabled) {
		background: hsl(var(--sb-destructive) / 0.12);
	}
	/* ── 학습자 정보 안의 소구역(레벨 받아오기 / 닉네임 / 그 밖의 항목) ── */
	.lp-sec {
		padding: 14px 0;
	}
	.lp-sec:first-child {
		padding-top: 0;
	}
	.lp-sec + .lp-sec {
		border-top: 1px dashed hsl(var(--sb-border));
	}
	.lp-sec:last-child {
		padding-bottom: 0;
	}
	.lp-sec-title {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 13px;
		font-weight: 600;
		color: hsl(var(--sb-foreground));
	}
	.lp-sec-desc {
		font-size: 12px;
		line-height: 1.6;
		color: hsl(var(--sb-muted-foreground));
		margin: 2px 0 10px;
	}
	.lp-hint,
	.lp-meta {
		font-size: 12px;
		line-height: 1.6;
		color: hsl(var(--sb-muted-foreground));
		margin-top: 8px;
	}
	.lp-hint b {
		color: hsl(var(--sb-foreground));
		font-weight: 500;
	}
	.lp-warn {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		font-size: 12px;
		color: hsl(var(--sb-destructive));
		margin-top: 8px;
	}
	.lp-select {
		max-width: 420px;
	}
	.lp-select-sm {
		width: 110px;
	}
	.lp-select-md {
		width: 190px;
	}
	.lp-field-label {
		font-size: 12px;
		font-weight: 600;
		color: hsl(var(--sb-foreground));
		margin: 14px 0 6px;
	}
	/* 레벨(또는 값) 칩 + 지시문 한 줄 — 지시문은 길어지면 칸이 늘어난다(autoGrow) */
	.lp-level-row {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		margin-bottom: 6px;
	}
	.lp-level-row textarea {
		flex: 1;
		min-height: 40px;
	}
	.lp-level-row button.mini {
		margin-top: 8px;
	}
	.lp-level {
		flex: none;
		min-width: 44px;
		height: 40px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0 10px;
		border-radius: calc(var(--sb-radius) - 2px);
		background: hsl(var(--sb-muted));
		font-size: 15px;
	}
	.lp-inline {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
		margin-top: 10px;
	}
	input.text-field.lp-short {
		flex: none;
		width: 140px;
	}
	.lp-item {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		padding: 10px;
		margin-bottom: 8px;
		background: hsl(var(--sb-background));
	}
	.lp-item.linked {
		border-left: 3px solid hsl(var(--sb-primary) / 0.6);
	}
	.lp-item-row {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
	}
	.lp-item .lp-item-row {
		margin-bottom: 10px;
	}
	.lp-key {
		flex: 0 0 160px;
	}
	.lp-val {
		flex: 0 0 120px;
	}
	.lp-item-opts {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px 18px;
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
	/* 콘솔은 preflight 가 꺼져 webflow 의 label{bold; mb:5px} 가 먹는다 — 직접 되돌린다 */
	.lp-switch {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		margin: 0;
		font-weight: 400;
		color: hsl(var(--sb-foreground));
		cursor: pointer;
	}
	.lp-opt {
		display: inline-flex;
		align-items: center;
		gap: 6px;
	}
	.lp-item-note {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
	.lp-item-hint {
		font-size: 11px;
		color: hsl(var(--sb-muted-foreground));
		margin-top: 8px;
	}
	/* 레벨 지시문에 대화 방식 규칙이 섞였을 때(stepLint LEVEL_GUIDE_RULE) — 행 바로 아래, 칩 폭만큼 들여서 */
	.lp-rule-warn {
		margin: -2px 0 8px 52px;
		font-size: 11px;
		line-height: 1.5;
		color: hsl(var(--sb-destructive));
	}
	/* 값별 지시문 — 어느 항목에 속한 값인지 눈으로 잡히게 왼쪽 선으로 묶는다 */
	.lp-map {
		margin-top: 10px;
		padding-left: 12px;
		border-left: 2px solid hsl(var(--sb-border));
	}
	@media (max-width: 640px) {
		.lp-key,
		.lp-val {
			flex-basis: 110px;
		}
	}
</style>
